<script setup lang="ts">
import {
    Archive,
    ArchiveRestore,
    ArrowLeft,
    Clock,
    Copy,
    FolderInput,
    Forward,
    Maximize2,
    PenLine,
    Reply,
    ReplyAll,
    Sparkles,
    Tag,
    Trash2,
} from '@lucide/vue'
import { createReusableTemplate } from '@vueuse/core'
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    Separator,
    ZhPanel,
    ZhStatusDot,
    ZhStatusPill,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import {
    CRAFTED_NOT_IN_PLAN_MESSAGE,
    isCraftedRefusal,
    type ListQuery,
    type MailAliasRow,
    type MailAliasSignatureSave,
    type MailAttachmentUpload,
    type MailBody,
    type MailboxActionOffer,
    type MailboxDestination,
    type MailContact,
    type MailContactQuery,
    type MailContactsFileFormat,
    type MailContactsImportResult,
    type MailMoveTarget,
    type MailReplyDraft,
    type MailSendBody,
    type MailSendOutcome,
    type MailSignature,
    type MailSignatureSave,
    type MemberEmailDetail,
    type MemberEmailHandledState,
    type MemberEmailList,
    type MemberEmailRow,
    type MemberEmailThreadMessage,
    type RunRow,
} from '@/api'
import MailAttachmentPicker from '@/components/MailAttachmentPicker.vue'
import MailboxActionSchedulePicker from '@/components/MailboxActionSchedulePicker.vue'
import MailContactsPanel from '@/components/MailContactsPanel.vue'
import MailEditor from '@/components/MailEditor.vue'
import MailHtmlFrame from '@/components/MailHtmlFrame.vue'
import MailQuotedText from '@/components/MailQuotedText.vue'
import MailRecipientsField from '@/components/MailRecipientsField.vue'
import MailSignaturePanel from '@/components/MailSignaturePanel.vue'
import MailSignatureToggle from '@/components/MailSignatureToggle.vue'
import { attachmentsRefusal, uploadsOf } from '@/lib/mailAttachments'
import { draftIsBlank, emptyDraft, type MailDraft } from '@/lib/mailDraft'
import { signatureInUse } from '@/lib/mailSignature'
import { statusLabel, statusTone } from '@/lib/statusTone'

/**
 * One mailbox, whoever holds it: a member's on their page, or the signed-in person's own on Inbox. The list a member's
 * page always had (Inbox, one tab per label, Archive, Sent; a page at a time; a mail opens with its text, its files
 * and the rest of its conversation, replies included), with the commands a mail client has wherever the holder may
 * act on the mail, and the one-click buttons the tasks that declare a mailbox action put on it. Everything it reads
 * and does goes through `source`: the panel knows no holder and no task, so the same panel reads a member's mailbox
 * read-only and works a person's. Given `emailId`, it shows that one mail on the whole page instead of the list.
 * Where the viewer may keep the mailbox's contacts (#1114), a Contacts tab lists them, and the To field of a new mail
 * and a forward offers them, with the enterprise's own people and members, as an address is typed. Where the viewer
 * may keep the mailbox's signature (#1115), a Signature tab edits it, and each mail being written shows it under the
 * editor with a box to leave it off. A reply shows the mail it answers, as it will be quoted below it (#1116), collapsed
 * under the editor, and can be sent without it. A new mail, a reply and a forward go to as many people as the writer
 * names, with Cc and Bcc a click away, and a received mail that went to others too can be answered to all of them
 * (#1119). A reply's To starts with the mail's sender, and the writer can change it.
 */

/** Who a forward goes to (#1119): one or more addresses in To, and any in Cc and Bcc. */
export type MailRecipients = { to: string[]; cc?: string[]; bcc?: string[] }

/** What the viewer may do to this mailbox's mail. Absent from a source, the mailbox reads only. */
export type MailboxActions = {
    /**
     * A reply's body says, beside the mail, who it goes to when not just the mail's sender (`to`), whether it answers
     * everyone (`replyAll`), and who else it copies in, seen and unseen.
     */
    reply: (emailId: string, body: MailReplyDraft, attachments: MailAttachmentUpload[]) => Promise<MailSendOutcome>
    forward: (
        emailId: string,
        recipients: MailRecipients,
        body: MailBody,
        attachments: MailAttachmentUpload[],
    ) => Promise<MailSendOutcome>
    send: (body: MailSendBody) => Promise<MailSendOutcome>
    setHandled: (emailId: string, handledState: MemberEmailHandledState, snoozedUntil?: string) => Promise<unknown>
    setLabels: (emailId: string, labels: string[]) => Promise<unknown>
    remove: (emailId: string) => Promise<unknown>
    /** Moves a mail, and the rest of its conversation, to another mailbox. Absent, mail stays where it is. */
    move?: (emailId: string, target: MailMoveTarget) => Promise<unknown>
    /** The mailboxes a mail here can be moved to, this one left out. */
    destinations?: () => Promise<MailboxDestination[]>
}

/**
 * The mailbox's contacts (#1114), where the viewer may keep them: a person's own always, a member's for an owner.
 * `list` with `directory` also offers the enterprise's people and members who have an address.
 */
export type MailboxContacts = {
    list: (query: MailContactQuery) => Promise<MailContact[]>
    save: (body: { address: string; name?: string }) => Promise<MailContact>
    rename: (id: string, name: string) => Promise<MailContact>
    remove: (id: string) => Promise<unknown>
    /** Imports a CSV or vCard file into the book (#1120), merged by address. */
    import: (file: MailAttachmentUpload) => Promise<MailContactsImportResult>
    /** Where the book downloads as a CSV or vCard file (#1120). */
    exportUrl: (format: MailContactsFileFormat) => string
}

/**
 * The mailbox's signature (#1115), where the viewer may keep it: a person's own always, a member's for an owner. The OS
 * adds it to everything the mailbox sends.
 */
export type MailboxSignature = {
    read: () => Promise<MailSignature>
    save: (body: MailSignatureSave) => Promise<MailSignature>
    clear: () => Promise<MailSignature>
    /**
     * The mailbox's extra addresses, each with its own signature (#1122), which mail that leaves from it carries in
     * place of the mailbox's. Absent, the Signature tab lists none, and every mail shows the mailbox's.
     */
    addresses?: MailboxAddressSignatures
}

/** The mailbox's extra addresses and their own signatures (#1122): listed, and each one's set or removed. */
export type MailboxAddressSignatures = {
    list: () => Promise<MailAliasRow[]>
    save: (aliasId: string, body: MailAliasSignatureSave) => Promise<MailAliasRow[]>
    clear: (aliasId: string) => Promise<MailAliasRow[]>
}

export type MailboxSource = {
    list: (query: ListQuery) => Promise<MemberEmailList>
    open: (emailId: string) => Promise<MemberEmailDetail>
    attachmentUrl: (emailId: string, attachmentId: string) => string
    actions?: MailboxActions
    /** The mailbox's contacts. Absent, there is no Contacts tab and a recipient is typed in full. */
    contacts?: MailboxContacts
    /** The mailbox's signature. Absent, there is no Signature tab and nothing is shown under the editor. */
    signature?: MailboxSignature
    /** The one-click buttons this mailbox offers, read when the panel opens. Absent offers none. */
    offers?: () => Promise<MailboxActionOffer[]>
    /**
     * Starts an offered task over this mailbox; `emailId` is the mail a button on one message was pressed on. A run
     * is model work, so the source asks the AI account guard first (#1796) and answers `null` when the person has no
     * AI account: nothing started, and the guard's prompt says why.
     */
    startAction?: (taskId: string, emailId?: string) => Promise<RunRow | null>
    /** Clones an offered template into the enterprise. Absent, a template's button only says where to find it. */
    cloneAction?: (catalogTaskId: string) => Promise<unknown>
    /**
     * Presses a button over the whole mailbox on the holder's own cadence (`cron`, in the enterprise's zone), or stops
     * it. Absent, the buttons are pressed by hand only.
     */
    scheduleAction?: {
        save: (taskId: string, cron: string) => Promise<unknown>
        clear: (taskId: string) => Promise<unknown>
    }
    /** Where one mail opens on a page of its own. Absent, a mail opens in a dialog only. */
    messageUrl?: (emailId: string) => string
    /** Where the mailbox's list is, for the way back from a mail on its own page. */
    listUrl?: string
}

const props = withDefaults(defineProps<{ source: MailboxSource; own?: boolean; emailId?: string }>(), {
    own: false,
    emailId: undefined,
})
/** Something changed the mailbox: a reply, a forward, a filing, a delete. The parent may refresh what it counts. */
const emit = defineEmits<{ changed: [] }>()

const limit = 25
const emails = ref<MemberEmailRow[]>([])
const total = ref(0)
const page = ref(1)
const pages = ref(1)
const labelCounts = ref<Array<{ label: string; count: number }>>([])
const unlabelledCount = ref(0)
const archivedCount = ref(0)
const sentCount = ref(0)
const selectedLabel = ref<string | null>(null)
/** The Archive tab: every closed mail that arrived, whatever its label. Never set together with a label. */
const archiveSelected = ref(false)
/** The Sent tab: everything the mailbox sent. Never set together with a label or the Archive. */
const sentSelected = ref(false)
/** The Contacts tab (#1114): the mailbox's contacts instead of its mail. Never set together with any other tab. */
const contactsSelected = ref(false)
/** The Signature tab (#1115): the mailbox's signature instead of its mail. Never set together with any other tab. */
const signatureSelected = ref(false)
const loading = ref(false)
const error = ref<string | null>(null)
const openEmail = ref<MemberEmailDetail | null>(null)

const canAct = computed(() => Boolean(props.source.actions))
/** The Inbox tab: none of the others. */
const inboxSelected = computed(
    () =>
        selectedLabel.value === null &&
        !archiveSelected.value &&
        !sentSelected.value &&
        !contactsSelected.value &&
        !signatureSelected.value,
)
/** One mail on the whole page, with no list around it. */
const fullPage = computed(() => Boolean(props.emailId))
const from = computed(() => (total.value === 0 ? 0 : (page.value - 1) * limit + 1))
const to = computed(() => Math.min(page.value * limit, total.value))
const tabName = computed(() => {
    if (signatureSelected.value) {
        return 'Signature'
    }
    if (contactsSelected.value) {
        return 'Contacts'
    }
    if (sentSelected.value) {
        return 'Sent'
    }
    return archiveSelected.value ? 'Archive' : (selectedLabel.value ?? 'Inbox')
})
const emptyText = computed(() => {
    if (sentSelected.value) {
        return 'Nothing sent yet.'
    }
    if (archiveSelected.value) {
        return 'Nothing archived yet.'
    }
    if (selectedLabel.value) {
        return `No open mail labelled ${selectedLabel.value}.`
    }
    return props.own ? 'Your inbox is clear.' : 'Inbox is clear.'
})

/** How many mails in the loaded page share each conversation, so a row can say it is part of one. */
const threadSizes = computed(() => {
    const sizes = new Map<string, number>()
    for (const row of emails.value) {
        if (row.threadId) {
            sizes.set(row.threadId, (sizes.get(row.threadId) ?? 0) + 1)
        }
    }
    return sizes
})

function threadSize(row: MemberEmailRow): number {
    return row.threadId ? (threadSizes.value.get(row.threadId) ?? 0) : 0
}

const HANDLED_STATE_TEXT: Record<Exclude<MemberEmailHandledState, 'unhandled'>, string> = {
    replied: 'Replied',
    skipped: 'Skipped',
    snoozed: 'Snoozed',
    escalated: 'Escalated',
}

/** Where a mail stands, or nothing while it is still unhandled. A snooze says until when. */
function handledStateText(row: Pick<MemberEmailRow, 'handledState' | 'snoozedUntil'>): string | null {
    if (!row.handledState || row.handledState === 'unhandled') {
        return null
    }
    const text = HANDLED_STATE_TEXT[row.handledState]
    if (row.handledState === 'snoozed' && row.snoozedUntil) {
        const until = new Date(row.snoozedUntil).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        })
        return `${text} until ${until}`
    }
    return text
}

/** Whether something is still owed on a mail: the states the Inbox and label tabs list. Archive lists the rest. */
function isOpenMail(row: Pick<MemberEmailRow, 'handledState'>): boolean {
    return row.handledState !== 'replied' && row.handledState !== 'skipped'
}

/** Whether the mailbox sent this mail: a copy of what went out, which nobody answers, files or snoozes. */
function isSent(row: Pick<MemberEmailRow, 'direction'>): boolean {
    return row.direction === 'outbound'
}

/** When a message arrived or left, as a person reads it. */
function sentAt(at: string): string {
    return new Date(at).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

/** Who a message in a conversation went between, as one line. */
function correspondents(message: MemberEmailThreadMessage): string {
    const recipients = message.to.length > 0 ? message.to.join(', ') : message.envelopeTo
    return `${message.fromName || message.fromAddress} to ${recipients}`
}

/** Inbox and the label tabs list open mail only; Archive lists everything closed that arrived; Sent what went out. */
function tabFilter(): Record<string, string> {
    if (sentSelected.value) {
        return { direction: 'outbound' }
    }
    if (archiveSelected.value) {
        return { open: 'false', direction: 'inbound' }
    }
    return selectedLabel.value ? { label: selectedLabel.value, open: 'true' } : { unlabelledOnly: 'true', open: 'true' }
}

function listQuery(requested: number): ListQuery {
    return { page: requested, limit, includeLabelCounts: 'true', ...tabFilter() }
}

function apply(mail: MemberEmailList): void {
    emails.value = mail.data
    total.value = mail.total
    page.value = mail.page
    pages.value = mail.pages
    labelCounts.value = mail.labelCounts ?? []
    unlabelledCount.value = mail.unlabelledCount ?? 0
    archivedCount.value = mail.archivedCount ?? 0
    sentCount.value = mail.sentCount ?? 0
    error.value = null
}

/** A label tab disappears once its last open mail is closed; the view then has no tab to stand on. */
function selectedLabelEmptied(): boolean {
    const label = selectedLabel.value
    return label !== null && total.value === 0 && !labelCounts.value.some(entry => entry.label === label)
}

async function load(requested: number): Promise<void> {
    loading.value = true
    error.value = null
    try {
        apply(await props.source.list(listQuery(requested)))
        if (selectedLabelEmptied()) {
            selectedLabel.value = null
            apply(await props.source.list(listQuery(1)))
        }
    } catch {
        emails.value = []
        total.value = 0
        page.value = 1
        pages.value = 1
        error.value = props.own ? 'Could not load your mail.' : 'Could not load mail.'
    } finally {
        loading.value = false
    }
}

/** The open mail read again, so its conversation shows what was just sent. A failed read keeps what it showed. */
async function refreshOpen(): Promise<void> {
    const mail = openEmail.value
    if (!mail) {
        return
    }
    try {
        openEmail.value = await props.source.open(mail.id)
    } catch {
        // The mail was already on screen; the next open reads it again.
    }
}

/** The page again after a change, stepping back when the change emptied the last page. On its own page, the mail. */
async function reload(): Promise<void> {
    if (fullPage.value) {
        await refreshOpen()
        emit('changed')
        return
    }
    const wanted = page.value
    await load(wanted)
    if (emails.value.length === 0 && wanted > 1 && !error.value) {
        await load(wanted - 1)
    }
    emit('changed')
}

async function selectTab(label: string | null, folder: 'archive' | 'sent' | null = null): Promise<void> {
    const archived = folder === 'archive'
    const sentOnly = folder === 'sent'
    if (
        !contactsSelected.value &&
        !signatureSelected.value &&
        selectedLabel.value === label &&
        archiveSelected.value === archived &&
        sentSelected.value === sentOnly
    ) {
        return
    }
    contactsSelected.value = false
    signatureSelected.value = false
    selectedLabel.value = label
    archiveSelected.value = archived
    sentSelected.value = sentOnly
    openEmail.value = null
    await load(1)
}

/** The Contacts tab: the mailbox's contacts in place of its mail, read by the tab itself. */
function selectContacts(): void {
    contactsSelected.value = true
    signatureSelected.value = false
    selectedLabel.value = null
    archiveSelected.value = false
    sentSelected.value = false
    openEmail.value = null
}

/** The Signature tab: the mailbox's signature in place of its mail, read by the tab itself. */
function selectSignature(): void {
    signatureSelected.value = true
    contactsSelected.value = false
    selectedLabel.value = null
    archiveSelected.value = false
    sentSelected.value = false
    openEmail.value = null
}

/**
 * The mailbox's signature, read as the panel opens where the viewer may write, and again whenever the Signature tab
 * saves it: shown under the editor of a reply, a forward and a new mail (#1115). Null where there is none to show.
 */
const signature = ref<MailSignature | null>(null)
/** The mailbox's extra addresses, each with its own signature (#1122), read with the mailbox's signature. */
const addressSignatures = ref<MailAliasRow[]>([])
/** Whether the mail being written carries the signature. On by default, and again after each send. */
const addSignature = ref(true)
/** Whether the reply being written quotes the mail it answers (#1116). On by default, and again for each reply. */
const addQuote = ref(true)

async function loadSignature(): Promise<void> {
    const source = props.source.signature
    if (!source || !props.source.actions) {
        return
    }
    try {
        signature.value = await source.read()
    } catch {
        // Nothing to show, then: the OS still adds it when the mail leaves.
        signature.value = null
    }
    try {
        addressSignatures.value = source.addresses ? await source.addresses.list() : []
    } catch {
        // The mailbox's own signature is shown under a reply, then; the OS still signs as the address it leaves from.
        addressSignatures.value = []
    }
}

/**
 * The signature a reply to the open mail carries, as the API decides it (#1122): a reply leaves from the address the
 * mail arrived at when that is an extra address that answers from itself, and is signed with that address's own
 * signature when it has one; otherwise with the mailbox's. A new mail and a forward leave from the mailbox's own
 * address, so they show the mailbox's.
 */
const replySignature = computed<MailSignature | null>(() => {
    const arrivedAt = openEmail.value?.envelopeTo.trim().toLowerCase() ?? ''
    const address = addressSignatures.value.find(row => row.address.toLowerCase() === arrivedAt)
    if (!address?.replyFromAlias || !address.signature) {
        return signature.value
    }
    const mailbox = signature.value ?? { html: '', text: '', updatedAt: null, useTemplate: true, template: null }
    return { ...mailbox, html: address.signature.html, text: address.signature.text }
})

/**
 * What the To field offers as an address is typed: the mailbox's contacts and the enterprise's own people and members
 * (#1114). Nothing where the source keeps no contacts.
 */
const recipientSearch = computed(() => {
    const contacts = props.source.contacts
    return contacts ? (text: string) => contacts.list({ search: text, directory: true, limit: 8 }) : undefined
})

async function open(emailId: string): Promise<void> {
    try {
        openEmail.value = await props.source.open(emailId)
        mode.value = 'read'
        actionError.value = null
        emails.value = emails.value.map(row =>
            row.id === emailId ? { ...row, readAt: row.readAt ?? new Date().toISOString() } : row,
        )
    } catch {
        error.value = props.own ? 'Could not open this mail.' : 'Could not open mail.'
    }
}

function closeOpen(): void {
    openEmail.value = null
    mode.value = 'read'
    actionError.value = null
}

/** Which of the mail's ids was last copied, so its button can say so. */
const copied = ref<string | null>(null)

async function copyId(value: string): Promise<void> {
    try {
        await navigator.clipboard.writeText(value)
        copied.value = value
    } catch {
        // No clipboard here: the id is on screen to select by hand.
    }
}

/*
 * The commands. Each is one call through `source.actions`, then the list read again so the row moves where the
 * command sent it (out of the Inbox, into the Archive). What happened is said once, above the list; why a command
 * was refused is said beside it, in the server's own sentence where it gave one.
 */
type Mode = 'read' | 'reply' | 'forward' | 'labels' | 'snooze' | 'move'
const mode = ref<Mode>('read')
/** The command in flight, so every button waits for it. */
const busy = ref<string | null>(null)
/** What the last command did, until the next one. */
const notice = ref<string | null>(null)
/** The run the last button started, so the notice can link to it. */
const noticeRun = ref<string | null>(null)
/** Why the last command in the open mail was refused. */
const actionError = ref<string | null>(null)
const replyDraft = ref<MailDraft>(emptyDraft())
const replyFiles = ref<File[]>([])
/** The address a reply leaves from, when the writer names one; blank, the OS picks the one the mail arrived at. */
const replyFrom = ref('')
/** The address a new mail leaves from, when the writer names one; blank, the mailbox's own. */
const composeFrom = ref('')
/** Where the open mail can be moved, read as the Move form opens, and the one picked (`member:<id>` or `person:<id>`). */
const moveDestinations = ref<MailboxDestination[]>([])
const moveTarget = ref('')
/**
 * Who the reply being written goes to (#1119): the mail's sender as the form opens, which the writer can change or add
 * to. Sent as `to` only once it is not just that sender, so an untouched reply answers the sender as it always has.
 */
const replyTo = ref<string[]>([])
/** Whether the reply being written answers everyone on the mail (#1119), and who it copies in, seen and unseen. */
const replyAll = ref(false)
const replyCc = ref<string[]>([])
const replyBcc = ref<string[]>([])
/** Whether a reply shows its Cc and Bcc fields: a reply-all opens with them, a reply when the writer asks. */
const replyCopies = ref(false)
/** The others a reply-all copies in as the form opened, so a Cc the writer changed is sent as they left it. */
const replyAllOthers = ref<string[]>([])
const forwardTo = ref<string[]>([])
const forwardCc = ref<string[]>([])
const forwardBcc = ref<string[]>([])
const forwardCopies = ref(false)
const forwardDraft = ref<MailDraft>(emptyDraft())
const forwardFiles = ref<File[]>([])
const labelsText = ref('')
const snoozeUntil = ref('')
const composeOpen = ref(false)
const composeTo = ref<string[]>([])
const composeCc = ref<string[]>([])
const composeBcc = ref<string[]>([])
/** Whether a new mail shows its Cc and Bcc fields: hidden until the writer asks for them, or one names somebody. */
const composeCopies = ref(false)
const composeSubject = ref('')
const composeDraft = ref<MailDraft>(emptyDraft())
const composeFiles = ref<File[]>([])
const composeError = ref<string | null>(null)
const pendingDelete = ref<Pick<MemberEmailRow, 'id' | 'subject'> | null>(null)

/** `lead`, and the server's sentence after it when the refusal carried one: a bare code adds nothing. */
function failureText(caught: unknown, lead: string): string {
    const said = caught instanceof Error ? caught.message.trim() : ''
    return /\s/.test(said) ? `${lead} ${said}` : lead
}

/** A send's outcome as a notice or a refusal: the provider's reason when it refused, else what was done. */
function settle(outcome: MailSendOutcome, done: string, lead: string): boolean {
    if (outcome.status === 'sent') {
        notice.value = done
        noticeRun.value = null
        return true
    }
    actionError.value = outcome.error ? `${lead} ${outcome.error}` : lead
    return false
}

async function command<T>(name: string, run: () => Promise<T>): Promise<T | undefined> {
    if (busy.value) {
        return undefined
    }
    busy.value = name
    actionError.value = null
    try {
        return await run()
    } finally {
        busy.value = null
    }
}

async function archive(mail: Pick<MemberEmailRow, 'id'>): Promise<void> {
    await setHandled(mail.id, 'skipped', 'Archived.', 'Could not archive this mail.')
}

async function restore(mail: Pick<MemberEmailRow, 'id'>): Promise<void> {
    await setHandled(mail.id, 'unhandled', 'Moved to the inbox.', 'Could not move this mail.')
}

async function setHandled(
    emailId: string,
    handledState: MemberEmailHandledState,
    done: string,
    lead: string,
    snoozedUntil?: string,
): Promise<void> {
    const actions = props.source.actions
    if (!actions) {
        return
    }
    await command(handledState, async () => {
        try {
            await actions.setHandled(emailId, handledState, snoozedUntil)
        } catch (caught) {
            actionError.value = failureText(caught, lead)
            return
        }
        if (openEmail.value?.id === emailId) {
            openEmail.value = { ...openEmail.value, handledState, snoozedUntil: snoozedUntil ?? null }
        }
        mode.value = 'read'
        notice.value = done
        noticeRun.value = null
        await reload()
    })
}

/**
 * What a draft sends: its text, and its HTML when the editor holds any (#1113), and `signature: false` when the writer
 * left the mailbox's signature, or the enterprise's template (#1118), off (#1115). Left on, the OS adds it as the mail
 * leaves.
 */
function bodyOf(draft: MailDraft, shown: MailSignature | null = signature.value): MailBody {
    const body: MailBody = draft.html.trim() === '' ? { text: draft.text } : { text: draft.text, html: draft.html }
    return signatureInUse(shown) && !addSignature.value ? { ...body, signature: false } : body
}

/**
 * What a reply sends: its draft's body, and `quote: false` when the writer removed the quoted text (#1116). Its To
 * (#1119) is sent as `to` only when the writer changed it from the mail's sender. A reply-all says `replyAll: true` and
 * adds whoever the writer copied in beyond the mail's own; one whose Cc the writer took somebody off names its Cc whole
 * instead, so the OS sends to exactly those. Its Bcc is sent when it names anyone.
 */
function replyBodyOf(mail: MemberEmailDetail, draft: MailDraft): MailReplyDraft {
    const signed = bodyOf(draft, replySignature.value)
    const body: MailReplyDraft = addQuote.value ? signed : { ...signed, quote: false }
    const others = replyAllOthers.value
    const named = replyCc.value.map(address => address.trim().toLowerCase())
    const asAll = replyAll.value && others.every(address => named.includes(address))
    const cc = asAll ? replyCc.value.filter((_, index) => !others.includes(named[index] ?? '')) : replyCc.value
    const sender = replyFrom.value.trim()
    return {
        ...body,
        ...(sender ? { from: sender } : {}),
        ...(replyGoesToSender(mail) ? {} : { to: replyTo.value }),
        ...(asAll ? { replyAll: true } : {}),
        ...(cc.length > 0 ? { cc } : {}),
        ...(replyBcc.value.length > 0 ? { bcc: replyBcc.value } : {}),
    }
}

/** Whether the reply being written goes to the mail's sender and nobody else, as the form opened it. */
function replyGoesToSender(mail: MemberEmailDetail): boolean {
    const addressed = replyTo.value.map(address => address.trim().toLowerCase())
    return addressed.length === 1 && addressed[0] === mail.fromAddress.trim().toLowerCase()
}

/**
 * Who the reply form says the reply goes to: the sender by name while To is theirs alone, else the addresses in To, and
 * everyone in Cc beside them when it names anyone.
 */
const replyHeading = computed(() => {
    const mail = openEmail.value
    if (!mail) {
        return 'Your reply'
    }
    const addressed = replyGoesToSender(mail) ? mail.fromName || mail.fromAddress : replyTo.value.join(', ')
    const lead = addressed ? `Your reply to ${addressed}` : 'Your reply'
    return replyCopies.value && replyCc.value.length > 0 ? `${lead} and everyone in Cc` : lead
})

/**
 * Everyone else an open mail went to (#1119): its To and Cc, each once, but for its sender and the address it arrived
 * at. The OS also leaves out the mailbox's other addresses when it sends, so this is what the reply-all form shows.
 */
function othersOn(mail: MemberEmailDetail): string[] {
    const own = new Set([mail.fromAddress, mail.envelopeTo].map(address => address.trim().toLowerCase()))
    const others: string[] = []
    for (const address of [...(mail.to ?? []), ...(mail.cc ?? [])]) {
        const normalised = address.trim().toLowerCase()
        if (normalised && !own.has(normalised) && !others.includes(normalised)) {
            others.push(normalised)
        }
    }
    return others
}

/**
 * Opens the reply form on the open mail, quoting it again whatever an earlier reply chose, with To back to the mail's
 * sender and Bcc empty; `all` answers everyone, its Cc and Bcc shown.
 */
function startReply(all = false): void {
    addQuote.value = true
    replyAll.value = all
    replyAllOthers.value = all && openEmail.value ? othersOn(openEmail.value) : []
    replyTo.value = openEmail.value ? [openEmail.value.fromAddress] : []
    replyCc.value = [...replyAllOthers.value]
    replyBcc.value = []
    replyFrom.value = ''
    replyCopies.value = all
    mode.value = 'reply'
}

async function sendReply(): Promise<void> {
    const mail = openEmail.value
    const actions = props.source.actions
    if (!mail || !actions || draftIsBlank(replyDraft.value) || replyTo.value.length === 0) {
        return
    }
    const refused = attachmentsRefusal(replyFiles.value)
    if (refused) {
        actionError.value = refused
        return
    }
    await command('reply', async () => {
        let outcome: MailSendOutcome
        try {
            outcome = await actions.reply(
                mail.id,
                replyBodyOf(mail, replyDraft.value),
                await uploadsOf(replyFiles.value),
            )
        } catch (caught) {
            actionError.value = failureText(caught, 'Could not send the reply.')
            return
        }
        if (
            settle(outcome, replyAll.value ? 'Reply sent to everyone.' : 'Reply sent.', 'The reply was not delivered.')
        ) {
            replyDraft.value = emptyDraft()
            replyFiles.value = []
            replyAll.value = false
            replyTo.value = []
            replyCc.value = []
            replyBcc.value = []
            replyCopies.value = false
            addSignature.value = true
            addQuote.value = true
            openEmail.value = { ...mail, handledState: 'replied', snoozedUntil: null }
            mode.value = 'read'
            // On its own page the mail is read again by `reload`; in the dialog, so the reply joins its conversation.
            if (!fullPage.value) {
                await refreshOpen()
            }
            await reload()
        }
    })
}

async function sendForward(): Promise<void> {
    const mail = openEmail.value
    const actions = props.source.actions
    if (!mail || !actions || forwardTo.value.length === 0) {
        return
    }
    const refused = attachmentsRefusal(forwardFiles.value)
    if (refused) {
        actionError.value = refused
        return
    }
    await command('forward', async () => {
        let outcome: MailSendOutcome
        try {
            outcome = await actions.forward(
                mail.id,
                recipientsOf(forwardTo.value, forwardCc.value, forwardBcc.value),
                bodyOf(forwardDraft.value),
                await uploadsOf(forwardFiles.value),
            )
        } catch (caught) {
            actionError.value = failureText(caught, 'Could not forward this mail.')
            return
        }
        if (settle(outcome, `Forwarded to ${forwardTo.value.join(', ')}.`, 'The forward was not delivered.')) {
            forwardTo.value = []
            forwardCc.value = []
            forwardBcc.value = []
            forwardCopies.value = false
            forwardDraft.value = emptyDraft()
            forwardFiles.value = []
            addSignature.value = true
            mode.value = 'read'
            if (!fullPage.value) {
                await refreshOpen()
            }
            await reload()
        }
    })
}

function startLabels(): void {
    labelsText.value = (openEmail.value?.labels ?? []).join(', ')
    mode.value = 'labels'
}

/** The labels a mail should carry, as typed: separated by commas, trimmed, without repeats or blanks. */
function parseLabels(text: string): string[] {
    const labels: string[] = []
    for (const part of text.split(',')) {
        const label = part.trim()
        if (label && !labels.includes(label)) {
            labels.push(label)
        }
    }
    return labels
}

async function saveLabels(): Promise<void> {
    const mail = openEmail.value
    const actions = props.source.actions
    if (!mail || !actions) {
        return
    }
    const labels = parseLabels(labelsText.value)
    await command('labels', async () => {
        try {
            await actions.setLabels(mail.id, labels)
        } catch (caught) {
            actionError.value = failureText(caught, 'Could not save the labels.')
            return
        }
        openEmail.value = { ...mail, labels }
        mode.value = 'read'
        notice.value = labels.length > 0 ? `Labelled ${labels.join(', ')}.` : 'Labels cleared.'
        noticeRun.value = null
        await reload()
    })
}

async function snooze(): Promise<void> {
    const mail = openEmail.value
    const until = new Date(snoozeUntil.value)
    if (!mail || snoozeUntil.value === '' || Number.isNaN(until.getTime())) {
        return
    }
    if (until.getTime() <= Date.now()) {
        actionError.value = 'Pick a time still to come.'
        return
    }
    const when = until.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    await setHandled(mail.id, 'snoozed', `Snoozed until ${when}.`, 'Could not snooze this mail.', until.toISOString())
}

/** Who a new mail or a forward goes to (#1119): To, and Cc and Bcc only when they name somebody. */
function recipientsOf(toList: string[], cc: string[], bcc: string[]): MailRecipients {
    return { to: toList, ...(cc.length > 0 ? { cc } : {}), ...(bcc.length > 0 ? { bcc } : {}) }
}

/** Whether a mail went to anyone beside the one address it is shown as reaching: a second To, a Cc or a Bcc. */
function hasCopies(mail: MemberEmailDetail): boolean {
    return (mail.to?.length ?? 0) > 1 || (mail.cc?.length ?? 0) > 0 || (mail.bcc?.length ?? 0) > 0
}

function openCompose(): void {
    composeError.value = null
    composeOpen.value = true
}

async function sendNew(): Promise<void> {
    const actions = props.source.actions
    if (!actions || composeTo.value.length === 0 || draftIsBlank(composeDraft.value)) {
        return
    }
    const refused = attachmentsRefusal(composeFiles.value)
    if (refused) {
        composeError.value = refused
        return
    }
    await command('send', async () => {
        composeError.value = null
        let outcome: MailSendOutcome
        try {
            const attachments = await uploadsOf(composeFiles.value)
            outcome = await actions.send({
                ...recipientsOf(composeTo.value, composeCc.value, composeBcc.value),
                subject: composeSubject.value.trim(),
                ...bodyOf(composeDraft.value),
                ...(composeFrom.value.trim() ? { from: composeFrom.value.trim() } : {}),
                ...(attachments.length > 0 ? { attachments } : {}),
            })
        } catch (caught) {
            composeError.value = failureText(caught, 'Could not send this mail.')
            return
        }
        if (outcome.status !== 'sent') {
            composeError.value = outcome.error
                ? `The mail was not delivered. ${outcome.error}`
                : 'The mail was not delivered.'
            return
        }
        notice.value = `Sent to ${composeTo.value.join(', ')}.`
        noticeRun.value = null
        composeOpen.value = false
        composeTo.value = []
        composeCc.value = []
        composeBcc.value = []
        composeCopies.value = false
        composeSubject.value = ''
        composeFrom.value = ''
        composeDraft.value = emptyDraft()
        composeFiles.value = []
        addSignature.value = true
        await reload()
    })
}

/** Opens the Move form on the open mail, with the mailboxes it can go to read afresh. */
async function startMove(): Promise<void> {
    const actions = props.source.actions
    if (!actions?.destinations) {
        return
    }
    moveTarget.value = ''
    actionError.value = null
    try {
        moveDestinations.value = await actions.destinations()
    } catch (caught) {
        actionError.value = failureText(caught, 'Could not list the mailboxes.')
        return
    }
    mode.value = 'move'
}

/** The mailbox picked in the Move form, as the API names one: `{ memberId }` or `{ userId }`. */
function moveTargetOf(value: string): { target: MailMoveTarget; mailbox: MailboxDestination } | null {
    const mailbox = moveDestinations.value.find(entry => `${entry.kind}:${entry.id}` === value)
    if (!mailbox) {
        return null
    }
    return { target: mailbox.kind === 'member' ? { memberId: mailbox.id } : { userId: mailbox.id }, mailbox }
}

async function confirmMove(): Promise<void> {
    const mail = openEmail.value
    const move = props.source.actions?.move
    const picked = moveTargetOf(moveTarget.value)
    if (!mail || !move || !picked) {
        return
    }
    await command('move', async () => {
        try {
            await move(mail.id, picked.target)
        } catch (caught) {
            actionError.value = failureText(caught, 'Could not move this mail.')
            return
        }
        closeOpen()
        notice.value = `Moved to ${picked.mailbox.name} (${picked.mailbox.address}).`
        noticeRun.value = null
        await reload()
    })
}

async function confirmDelete(): Promise<void> {
    const mail = pendingDelete.value
    const actions = props.source.actions
    if (!mail || !actions) {
        return
    }
    await command('delete', async () => {
        try {
            await actions.remove(mail.id)
        } catch (caught) {
            actionError.value = failureText(caught, 'Could not delete this mail.')
            pendingDelete.value = null
            return
        }
        pendingDelete.value = null
        if (openEmail.value?.id === mail.id) {
            closeOpen()
        }
        notice.value = 'Deleted.'
        noticeRun.value = null
        await reload()
    })
}

/*
 * The one-click buttons: each a task that declares a mailbox action (`source.offers`), read once as the panel opens.
 * A task the enterprise holds starts over this mailbox; a template nobody cloned yet is offered to clone first, and
 * once cloned, to start. The panel never names a task: the label, like the task, is the declaration's own.
 */
const offers = ref<MailboxActionOffer[]>([])
const mailboxOffers = computed(() => offers.value.filter(offer => offer.on === 'mailbox'))
const mailOffers = computed(() => offers.value.filter(offer => offer.on === 'mail'))
/** The template button pressed, while the clone is offered; the mail it was pressed on, for a button on one mail. */
const clonePrompt = ref<{ offer: MailboxActionOffer; emailId?: string } | null>(null)
const cloneState = ref<'idle' | 'cloning' | 'cloned'>('idle')
const cloneError = ref<string | null>(null)
/** The task the clone made, once the offers are read again: what "Run it now" starts. */
const clonedTaskId = ref<string | null>(null)

async function loadOffers(): Promise<void> {
    if (!props.source.offers) {
        return
    }
    try {
        offers.value = await props.source.offers()
    } catch {
        // No buttons, then: the mailbox itself still reads.
        offers.value = []
    }
}

async function runOffer(offer: MailboxActionOffer, emailId?: string): Promise<void> {
    if (!offer.taskId) {
        clonePrompt.value = { offer, emailId }
        cloneState.value = 'idle'
        cloneError.value = null
        clonedTaskId.value = null
        return
    }
    await startTask(offer.taskId, offer.name, emailId)
}

/** Puts a button over the whole mailbox on the holder's own cadence, or stops it (`source.scheduleAction`). */
function scheduleOffer(offer: MailboxActionOffer, cron: string): Promise<unknown> {
    const schedule = props.source.scheduleAction
    return offer.taskId && schedule ? schedule.save(offer.taskId, cron) : Promise.resolve()
}

function unscheduleOffer(offer: MailboxActionOffer): Promise<unknown> {
    const schedule = props.source.scheduleAction
    return offer.taskId && schedule ? schedule.clear(offer.taskId) : Promise.resolve()
}

async function startTask(taskId: string, name: string, emailId?: string): Promise<void> {
    const start = props.source.startAction
    if (!start) {
        return
    }
    await command(`start:${taskId}`, async () => {
        try {
            const run = await start(taskId, emailId)
            if (!run) {
                return
            }
            notice.value = run.status === 'pending' ? `${name} will start once its member is free.` : `Started ${name}.`
            noticeRun.value = run.id
            clonePrompt.value = null
        } catch (caught) {
            const text = failureText(caught, `Could not start ${name}.`)
            if (emailId && openEmail.value?.id === emailId) {
                actionError.value = text
            } else {
                notice.value = text
                noticeRun.value = null
            }
        }
    })
}

async function cloneOffer(): Promise<void> {
    const prompt = clonePrompt.value
    const clone = props.source.cloneAction
    if (!prompt?.offer.catalogTaskId || !clone || cloneState.value !== 'idle') {
        return
    }
    cloneState.value = 'cloning'
    cloneError.value = null
    try {
        await clone(prompt.offer.catalogTaskId)
    } catch (caught) {
        cloneError.value = isCraftedRefusal(caught)
            ? CRAFTED_NOT_IN_PLAN_MESSAGE
            : failureText(caught, 'Could not clone it.')
        cloneState.value = 'idle'
        return
    }
    await loadOffers()
    clonedTaskId.value = offers.value.find(offer => offer.slug === prompt.offer.slug && offer.taskId)?.taskId ?? null
    cloneState.value = 'cloned'
}

async function runCloned(): Promise<void> {
    const prompt = clonePrompt.value
    if (!prompt || !clonedTaskId.value) {
        return
    }
    await startTask(clonedTaskId.value, prompt.offer.name, prompt.emailId)
}

onMounted(() => {
    if (props.emailId) {
        void open(props.emailId)
    } else {
        void load(1)
    }
    void loadOffers()
    void loadSignature()
})

// On its own page, a message of the conversation opens by its address: the page follows it.
watch(
    () => props.emailId,
    emailId => {
        if (emailId && emailId !== openEmail.value?.id) {
            void open(emailId)
        }
    },
)

/** The open mail, drawn once and shown in the dialog or on its own page. */
const [DefineMessage, ReuseMessage] = createReusableTemplate()
</script>

<template>
	<div class="space-y-3" data-testid="mailbox">
		<!-- The open mail, drawn once: in the dialog over the list, or on a page of its own. -->
		<DefineMessage>
			<template v-if="openEmail">
				<!-- Everyone the mail went to (#1119), when that is more than the one address above; Bcc on a sent copy only. -->
				<dl v-if="hasCopies(openEmail)" class="text-muted-foreground grid grid-cols-[auto_1fr] gap-x-2 text-xs" data-testid="mail-recipients">
					<template v-if="openEmail.to.length">
						<dt>To</dt>
						<dd data-testid="mail-recipients-to">{{ openEmail.to.join(', ') }}</dd>
					</template>
					<template v-if="openEmail.cc.length">
						<dt>Cc</dt>
						<dd data-testid="mail-recipients-cc">{{ openEmail.cc.join(', ') }}</dd>
					</template>
					<template v-if="openEmail.bcc?.length">
						<dt>Bcc</dt>
						<dd data-testid="mail-recipients-bcc">{{ openEmail.bcc.join(', ') }}</dd>
					</template>
				</dl>
				<!-- What can be done with this mail: the mail client's commands, then the buttons its tasks declare. -->
				<div v-if="canAct || (mailOffers.length && !isSent(openEmail))" class="flex flex-wrap gap-2" data-testid="mail-actions">
					<template v-if="canAct">
						<Button v-if="!isSent(openEmail)" type="button" size="sm" variant="outline" data-testid="mail-reply" :disabled="busy !== null" @click="startReply()">
							<Reply class="size-3.5" aria-hidden="true" />
							Reply
						</Button>
						<Button
							v-if="!isSent(openEmail) && othersOn(openEmail).length > 0"
							type="button"
							size="sm"
							variant="outline"
							data-testid="mail-reply-all"
							:disabled="busy !== null"
							@click="startReply(true)"
						>
							<ReplyAll class="size-3.5" aria-hidden="true" />
							Reply all
						</Button>
						<Button type="button" size="sm" variant="outline" data-testid="mail-forward" :disabled="busy !== null" @click="mode = 'forward'">
							<Forward class="size-3.5" aria-hidden="true" />
							Forward
						</Button>
						<template v-if="!isSent(openEmail)">
							<Button
								v-if="isOpenMail(openEmail)"
								type="button"
								size="sm"
								variant="outline"
								data-testid="mail-archive"
								:disabled="busy !== null"
								@click="archive(openEmail)"
							>
								<Archive class="size-3.5" aria-hidden="true" />
								Archive
							</Button>
							<Button
								v-else
								type="button"
								size="sm"
								variant="outline"
								data-testid="mail-restore"
								:disabled="busy !== null"
								@click="restore(openEmail)"
							>
								<ArchiveRestore class="size-3.5" aria-hidden="true" />
								Move to inbox
							</Button>
						</template>
						<Button type="button" size="sm" variant="outline" data-testid="mail-labels" :disabled="busy !== null" @click="startLabels">
							<Tag class="size-3.5" aria-hidden="true" />
							Labels
						</Button>
						<Button v-if="!isSent(openEmail)" type="button" size="sm" variant="outline" data-testid="mail-snooze" :disabled="busy !== null" @click="mode = 'snooze'">
							<Clock class="size-3.5" aria-hidden="true" />
							Snooze
						</Button>
						<Button v-if="source.actions?.move && source.actions.destinations" type="button" size="sm" variant="outline" data-testid="mail-move" :disabled="busy !== null" @click="startMove">
							<FolderInput class="size-3.5" aria-hidden="true" />
							Move
						</Button>
						<Button type="button" size="sm" variant="ghost" data-testid="mail-delete" :disabled="busy !== null" @click="pendingDelete = openEmail">
							<Trash2 class="size-3.5" aria-hidden="true" />
							Delete
						</Button>
					</template>
					<template v-if="!isSent(openEmail)">
						<Button
							v-for="offer in mailOffers"
							:key="offer.slug"
							type="button"
							size="sm"
							:data-mail-action="offer.slug"
							:data-installed="offer.taskId ? 'true' : 'false'"
							:disabled="busy !== null"
							:title="offer.taskId ? `Start ${offer.name} on this mail` : `${offer.name} is in the Catalog: clone it to use this`"
							@click="runOffer(offer, openEmail.id)"
						>
							<Sparkles class="size-3.5" aria-hidden="true" />
							{{ offer.label }}
						</Button>
					</template>
				</div>
				<form v-if="mode === 'reply'" class="space-y-2" data-testid="mail-reply-form" @submit.prevent="sendReply">
					<div class="grid gap-1 text-sm">
						<div class="flex items-center justify-between">
							<span>To</span>
							<button v-if="!replyCopies" type="button" class="text-muted-foreground text-xs underline underline-offset-2" data-testid="mail-reply-copies" @click="replyCopies = true">Cc/Bcc</button>
						</div>
						<MailRecipientsField v-model="replyTo" :search="recipientSearch" :disabled="busy !== null" required label="To" testid="mail-reply-to" />
					</div>
					<template v-if="replyCopies">
						<div class="grid gap-1 text-sm" data-testid="mail-reply-cc">
							<span>Cc</span>
							<MailRecipientsField v-model="replyCc" :search="recipientSearch" :disabled="busy !== null" label="Cc" testid="mail-reply-cc-input" />
						</div>
						<div class="grid gap-1 text-sm">
							<span>Bcc</span>
							<MailRecipientsField v-model="replyBcc" :search="recipientSearch" :disabled="busy !== null" label="Bcc" testid="mail-reply-bcc" />
						</div>
					</template>
					<label class="grid gap-1 text-sm">
						From
						<Input v-model="replyFrom" :placeholder="openEmail.envelopeTo" :disabled="busy !== null" data-testid="mail-reply-from" />
					</label>
					<div class="grid gap-1 text-sm">
						<span data-testid="mail-reply-heading">{{ replyHeading }}</span>
						<MailEditor v-model="replyDraft" label="Your reply" :disabled="busy !== null" testid="mail-reply-text" />
					</div>
					<MailSignatureToggle v-model="addSignature" :signature="replySignature" :disabled="busy !== null" testid="mail-reply-signature" />
					<MailQuotedText v-model="addQuote" :mail="openEmail" :disabled="busy !== null" testid="mail-reply-quote" />
					<MailAttachmentPicker v-model="replyFiles" :disabled="busy !== null" />
					<div class="flex gap-2">
						<Button type="submit" size="sm" :disabled="busy !== null || draftIsBlank(replyDraft) || replyTo.length === 0">Send reply</Button>
						<Button type="button" size="sm" variant="outline" :disabled="busy !== null" @click="mode = 'read'">Cancel</Button>
					</div>
				</form>
				<form v-else-if="mode === 'forward'" class="space-y-2" data-testid="mail-forward-form" @submit.prevent="sendForward">
					<div class="grid gap-1 text-sm">
						<div class="flex items-center justify-between">
							<span>To</span>
							<button v-if="!forwardCopies" type="button" class="text-muted-foreground text-xs underline underline-offset-2" data-testid="mail-forward-copies" @click="forwardCopies = true">Cc/Bcc</button>
						</div>
						<MailRecipientsField v-model="forwardTo" :search="recipientSearch" :disabled="busy !== null" required label="To" testid="mail-forward-to" />
					</div>
					<template v-if="forwardCopies">
						<div class="grid gap-1 text-sm">
							<span>Cc</span>
							<MailRecipientsField v-model="forwardCc" :search="recipientSearch" :disabled="busy !== null" label="Cc" testid="mail-forward-cc" />
						</div>
						<div class="grid gap-1 text-sm">
							<span>Bcc</span>
							<MailRecipientsField v-model="forwardBcc" :search="recipientSearch" :disabled="busy !== null" label="Bcc" testid="mail-forward-bcc" />
						</div>
					</template>
					<div class="grid gap-1 text-sm">
						<span>A note above the message</span>
						<MailEditor v-model="forwardDraft" label="A note above the message" :disabled="busy !== null" testid="mail-forward-text" />
					</div>
					<MailSignatureToggle v-model="addSignature" :signature="signature" :disabled="busy !== null" testid="mail-forward-signature" />
					<MailAttachmentPicker v-model="forwardFiles" :disabled="busy !== null" />
					<p class="text-muted-foreground text-xs">The message is forwarded with its formatting; its own attachments are not.</p>
					<div class="flex gap-2">
						<Button type="submit" size="sm" :disabled="busy !== null || forwardTo.length === 0">Forward</Button>
						<Button type="button" size="sm" variant="outline" :disabled="busy !== null" @click="mode = 'read'">Cancel</Button>
					</div>
				</form>
				<form v-else-if="mode === 'labels'" class="space-y-2" data-testid="mail-labels-form" @submit.prevent="saveLabels">
					<label class="grid gap-1 text-sm">
						Labels, separated by commas
						<Input v-model="labelsText" data-testid="mail-labels-text" />
					</label>
					<div class="flex gap-2">
						<Button type="submit" size="sm" :disabled="busy !== null">Save labels</Button>
						<Button type="button" size="sm" variant="outline" :disabled="busy !== null" @click="mode = 'read'">Cancel</Button>
					</div>
				</form>
				<form v-else-if="mode === 'move'" class="space-y-2" data-testid="mail-move-form" @submit.prevent="confirmMove">
					<label class="grid gap-1 text-sm">
						Move this mail, and the rest of its conversation, to
						<select v-model="moveTarget" required class="border-input bg-background w-full rounded-md border px-2 py-1 text-sm" data-testid="mail-move-target">
							<option value="" disabled>Pick a mailbox…</option>
							<optgroup v-if="moveDestinations.some(entry => entry.kind === 'person')" label="People">
								<option v-for="entry in moveDestinations.filter(entry => entry.kind === 'person')" :key="entry.id" :value="`person:${entry.id}`">{{ entry.name }} · {{ entry.address }}</option>
							</optgroup>
							<optgroup v-if="moveDestinations.some(entry => entry.kind === 'member')" label="Members">
								<option v-for="entry in moveDestinations.filter(entry => entry.kind === 'member')" :key="entry.id" :value="`member:${entry.id}`">{{ entry.name }} · {{ entry.address }}</option>
							</optgroup>
						</select>
					</label>
					<p class="text-muted-foreground text-xs">It leaves this mailbox: whoever holds the one it moves to reads and answers it from then on.</p>
					<div class="flex gap-2">
						<Button type="submit" size="sm" data-testid="mail-move-confirm" :disabled="busy !== null || moveTarget === ''">Move</Button>
						<Button type="button" size="sm" variant="outline" :disabled="busy !== null" @click="mode = 'read'">Cancel</Button>
					</div>
				</form>
				<form v-else-if="mode === 'snooze'" class="space-y-2" data-testid="mail-snooze-form" @submit.prevent="snooze">
					<label class="grid gap-1 text-sm">
						Bring it back on
						<Input v-model="snoozeUntil" type="datetime-local" required data-testid="mail-snooze-until" />
					</label>
					<div class="flex gap-2">
						<Button type="submit" size="sm" :disabled="busy !== null || snoozeUntil === ''">Snooze</Button>
						<Button type="button" size="sm" variant="outline" :disabled="busy !== null" @click="mode = 'read'">Cancel</Button>
					</div>
				</form>
				<p v-if="actionError" class="text-destructive text-sm" data-testid="mail-action-error">{{ actionError }}</p>
				<div class="flex flex-wrap items-center gap-2">
					<ZhSystemLabel v-if="isSent(openEmail)" as="span" data-sent>Sent</ZhSystemLabel>
					<ZhStatusPill v-if="openEmail.deliveryStatus" :tone="statusTone(openEmail.deliveryStatus)" :pulse="false">
						{{ statusLabel(openEmail.deliveryStatus) }}
					</ZhStatusPill>
					<ZhStatusPill v-if="openEmail.spamFlag" tone="failed" :pulse="false">Spam · {{ openEmail.spamScore }}</ZhStatusPill>
					<ZhSystemLabel v-if="!isSent(openEmail) && handledStateText(openEmail)" as="span" data-handled-state>{{ handledStateText(openEmail) }}</ZhSystemLabel>
					<ZhSystemLabel v-for="label in openEmail.labels ?? []" :key="label" as="span" tone="system" data-label>{{ label }}</ZhSystemLabel>
				</div>
				<p v-if="openEmail.deliveryStatus === 'unknown'" class="text-muted-foreground text-sm">
					Delivery has not been confirmed. Check again before resending to avoid a duplicate.
				</p>
				<!-- The whole conversation, oldest first: what arrived and what was sent back. -->
				<section v-if="(openEmail.thread?.length ?? 0) > 1" class="space-y-2" data-testid="mail-thread">
					<ZhSystemLabel as="h3" tone="muted">Conversation · {{ openEmail.thread.length }} messages</ZhSystemLabel>
					<ol class="border-border divide-border divide-y rounded border">
						<li v-for="message in openEmail.thread" :key="message.id" :data-thread-message="message.id" :data-direction="message.direction">
							<div v-if="message.id === openEmail.id" aria-current="true" class="bg-muted/50 flex flex-col gap-0.5 px-3 py-2 text-sm">
								<span class="flex flex-wrap items-center gap-2">
									<ZhSystemLabel as="span" :tone="message.direction === 'outbound' ? 'system' : 'muted'">
										{{ message.direction === 'outbound' ? 'Sent' : 'Received' }}
									</ZhSystemLabel>
									<span class="font-medium">{{ correspondents(message) }}</span>
									<span class="text-muted-foreground text-xs">{{ sentAt(message.receivedAt) }} · this message</span>
								</span>
							</div>
							<component
								:is="fullPage && source.messageUrl ? RouterLink : 'button'"
								v-else
								v-bind="fullPage && source.messageUrl ? { to: source.messageUrl(message.id) } : { type: 'button' }"
								class="hover:bg-muted/50 flex w-full flex-col gap-0.5 px-3 py-2 text-left text-sm"
								@click="fullPage && source.messageUrl ? undefined : open(message.id)"
							>
								<span class="flex flex-wrap items-center gap-2">
									<ZhSystemLabel as="span" :tone="message.direction === 'outbound' ? 'system' : 'muted'">
										{{ message.direction === 'outbound' ? 'Sent' : 'Received' }}
									</ZhSystemLabel>
									<span class="font-medium">{{ correspondents(message) }}</span>
									<span class="text-muted-foreground text-xs">{{ sentAt(message.receivedAt) }}</span>
									<ZhStatusPill v-if="message.deliveryStatus" :tone="statusTone(message.deliveryStatus)" :pulse="false">
										{{ statusLabel(message.deliveryStatus) }}
									</ZhStatusPill>
								</span>
								<span class="text-muted-foreground truncate">{{ message.snippet }}</span>
							</component>
						</li>
					</ol>
				</section>
				<!-- The mail as its sender laid it out, in a frame where nothing in it runs (#1112). -->
				<MailHtmlFrame v-if="openEmail.htmlBody" :html="openEmail.htmlBody" />
				<pre v-else class="whitespace-pre-wrap text-sm">{{ openEmail.textBody }}</pre>
				<ul v-if="openEmail.attachments.length" class="space-y-1 text-sm">
					<li v-for="file in openEmail.attachments" :key="file.id">
						<a class="underline-offset-2 hover:underline" :href="source.attachmentUrl(openEmail.id, file.id)">
							{{ file.filename }}
						</a>
					</li>
				</ul>
				<!-- The ids a task, a tool or a person names this mail by. -->
				<dl class="text-muted-foreground grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1 text-xs" data-testid="mail-ids">
					<dt>Mail ID</dt>
					<dd class="flex items-center gap-1 font-mono break-all">
						<span data-mail-id>{{ openEmail.id }}</span>
						<button type="button" class="hover:text-foreground" :aria-label="copied === openEmail.id ? 'Copied' : 'Copy the mail ID'" data-testid="mail-copy-id" @click="copyId(openEmail.id)">
							<Copy class="size-3" aria-hidden="true" />
						</button>
						<span v-if="copied === openEmail.id" aria-live="polite">Copied</span>
					</dd>
					<template v-if="openEmail.messageId">
						<dt>Message-ID</dt>
						<dd class="font-mono break-all" data-message-id>{{ openEmail.messageId }}</dd>
					</template>
					<template v-if="openEmail.threadId">
						<dt>Conversation</dt>
						<dd class="font-mono break-all">{{ openEmail.threadId }}</dd>
					</template>
				</dl>
			</template>
		</DefineMessage>

		<!-- One mail on the whole page, with the way back to the list. -->
		<template v-if="fullPage">
			<RouterLink v-if="source.listUrl" :to="source.listUrl" class="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm" data-testid="mail-back">
				<ArrowLeft class="size-3.5" aria-hidden="true" />
				Back to the mailbox
			</RouterLink>
			<p v-if="notice" class="text-sm" data-testid="mail-notice" aria-live="polite">
				{{ notice }}
				<RouterLink v-if="noticeRun" :to="`/runs/${noticeRun}`" class="underline underline-offset-2">Open the run</RouterLink>
			</p>
			<p v-if="error" class="text-destructive text-sm" data-testid="mail-error">{{ error }}</p>
			<article v-else-if="openEmail" class="space-y-4" data-testid="mail-page">
				<header class="space-y-1">
					<h2 class="text-xl font-semibold">{{ openEmail.subject || '(no subject)' }}</h2>
					<p class="text-muted-foreground text-sm">
						From {{ openEmail.fromName || openEmail.fromAddress }} to {{ openEmail.envelopeTo }} · {{ sentAt(openEmail.receivedAt) }}
					</p>
				</header>
				<ReuseMessage />
			</article>
			<p v-else-if="!notice" class="text-muted-foreground text-sm">Loading mail…</p>
		</template>

		<template v-else>
			<div class="flex flex-wrap items-start justify-between gap-3">
				<div role="tablist" :aria-label="own ? 'Your mail' : 'Mailbox labels'" class="flex flex-wrap gap-2">
					<Button
						type="button"
						size="sm"
						:variant="inboxSelected ? 'default' : 'outline'"
						role="tab"
						data-mail-tab="inbox"
						:aria-selected="inboxSelected"
						:disabled="loading"
						@click="selectTab(null)"
					>
						Inbox ({{ unlabelledCount }})
					</Button>
					<Button
						v-for="entry in labelCounts"
						:key="entry.label"
						type="button"
						size="sm"
						:variant="selectedLabel === entry.label ? 'default' : 'outline'"
						role="tab"
						:data-mail-tab="entry.label"
						:aria-selected="selectedLabel === entry.label"
						:disabled="loading"
						@click="selectTab(entry.label)"
					>
						{{ entry.label }} ({{ entry.count }})
					</Button>
					<Button
						type="button"
						size="sm"
						:variant="archiveSelected ? 'default' : 'outline'"
						role="tab"
						data-mail-archive-tab
						:aria-selected="archiveSelected"
						:disabled="loading"
						@click="selectTab(null, 'archive')"
					>
						Archive ({{ archivedCount }})
					</Button>
					<Button
						type="button"
						size="sm"
						:variant="sentSelected ? 'default' : 'outline'"
						role="tab"
						data-mail-sent-tab
						:aria-selected="sentSelected"
						:disabled="loading"
						@click="selectTab(null, 'sent')"
					>
						Sent ({{ sentCount }})
					</Button>
					<Button
						v-if="source.contacts"
						type="button"
						size="sm"
						:variant="contactsSelected ? 'default' : 'outline'"
						role="tab"
						data-mail-contacts-tab
						:aria-selected="contactsSelected"
						@click="selectContacts"
					>
						Contacts
					</Button>
						<Button
							v-if="source.signature"
							type="button"
							size="sm"
							:variant="signatureSelected ? 'default' : 'outline'"
							role="tab"
							data-mail-signature-tab
							:aria-selected="signatureSelected"
							@click="selectSignature"
						>
							Signature
						</Button>
				</div>
				<!-- The mailbox's own commands: the buttons its tasks declare, then a new mail where the holder may send. -->
				<div v-if="canAct || mailboxOffers.length" class="flex flex-wrap gap-2" data-testid="mailbox-toolbar">
					<span v-for="offer in mailboxOffers" :key="offer.slug" class="inline-flex items-center gap-1">
						<Button
							type="button"
							size="sm"
							variant="outline"
							:data-mailbox-action="offer.slug"
							:data-installed="offer.taskId ? 'true' : 'false'"
							:disabled="busy !== null"
							:title="offer.taskId ? `Start ${offer.name}` : `${offer.name} is in the Catalog: clone it to use this`"
							@click="runOffer(offer)"
						>
							<Sparkles class="size-3.5" aria-hidden="true" />
							{{ offer.label }}
						</Button>
						<!-- A person's own inbox runs a button on a cadence of their own too, over their own mailbox. -->
						<MailboxActionSchedulePicker
							v-if="offer.taskId && source.scheduleAction"
							:label="offer.label"
							:schedule="offer.schedule"
							:disabled="busy !== null"
							:save="cron => scheduleOffer(offer, cron)"
							:clear="() => unscheduleOffer(offer)"
							@changed="loadOffers"
							@failed="message => (notice = message)"
						/>
					</span>
					<Button v-if="canAct" type="button" size="sm" data-testid="mail-compose" :disabled="busy !== null" @click="openCompose">
						<PenLine class="size-3.5" aria-hidden="true" />
						New mail
					</Button>
				</div>
			</div>
			<p v-if="notice" class="text-sm" data-testid="mail-notice" aria-live="polite">
				{{ notice }}
				<RouterLink v-if="noticeRun" :to="`/runs/${noticeRun}`" class="underline underline-offset-2">Open the run</RouterLink>
			</p>
			<div v-if="contactsSelected && source.contacts" role="tabpanel" aria-label="Contacts">
				<MailContactsPanel :contacts="source.contacts" :own="own" />
			</div>
				<div v-else-if="signatureSelected && source.signature" role="tabpanel" aria-label="Signature">
					<MailSignaturePanel
						:signature="source.signature"
						:own="own"
						@saved="saved => (signature = saved)"
						@addresses="saved => (addressSignatures = saved)"
					/>
				</div>
			<div v-else role="tabpanel" :aria-label="tabName" :aria-busy="loading" class="space-y-3">
				<p v-if="error" class="text-destructive text-sm" data-testid="mail-error">{{ error }}</p>
				<p v-else-if="loading" class="text-muted-foreground text-sm">Loading mail…</p>
				<p v-else-if="!emails.length" class="text-muted-foreground text-sm" data-testid="mail-empty">
					{{ emptyText }}
				</p>
				<!-- The mail, on a kit surface of its own; each row draws its own padding, so the panel is flush. -->
				<ZhPanel v-else as="div" flush>
					<ul>
						<li v-for="(row, index) in emails" :key="row.id">
							<Separator v-if="index > 0" />
							<div class="group flex items-stretch">
								<button
									type="button"
									:data-email-id="row.id"
									:data-thread-id="row.threadId ?? undefined"
									class="flex min-w-0 flex-1 flex-col gap-1 px-3 py-2 text-left text-sm"
									@click="open(row.id)"
								>
									<span class="flex items-center justify-between gap-2">
										<span class="font-medium underline-offset-2 group-hover:underline">
											{{ row.subject || '(no subject)' }}
											<ZhStatusPill v-if="row.deliveryStatus" :tone="statusTone(row.deliveryStatus)" :pulse="false">
												{{ statusLabel(row.deliveryStatus) }}
											</ZhStatusPill>
											<ZhStatusPill v-if="row.spamFlag" tone="failed" :pulse="false" :title="`Spam score: ${row.spamScore}`">
												Spam
											</ZhStatusPill>
										</span>
										<span v-if="!row.readAt" title="Unread" data-unread><ZhStatusDot state="system" :pulse="false" /></span>
									</span>
									<span class="text-muted-foreground truncate">
										<template v-if="isSent(row)">To {{ row.to.length ? row.to.join(', ') : '(no recipient)' }}</template>
										<template v-else>{{ row.fromName || row.fromAddress }}</template>
										· {{ row.snippet }}
									</span>
									<span
										v-if="row.labels?.length || (!isSent(row) && handledStateText(row)) || threadSize(row) > 1"
										class="flex flex-wrap items-center gap-2 text-xs"
									>
										<ZhSystemLabel v-if="!isSent(row) && handledStateText(row)" as="span" data-handled-state>
											{{ handledStateText(row) }}
										</ZhSystemLabel>
										<ZhSystemLabel v-for="label in row.labels ?? []" :key="label" as="span" tone="system" data-label>
											{{ label }}
										</ZhSystemLabel>
										<span v-if="threadSize(row) > 1" data-thread-size class="text-muted-foreground">
											{{ threadSize(row) }} in conversation
										</span>
									</span>
								</button>
								<!-- A row's quick commands, shown as the pointer or focus reaches it; the rest are on the open mail. -->
								<div
									v-if="canAct"
									class="flex shrink-0 items-center gap-1 pr-2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 motion-reduce:transition-none"
								>
									<template v-if="!isSent(row)">
										<Button
											v-if="isOpenMail(row)"
											type="button"
											size="sm"
											variant="ghost"
											data-row-archive
											:aria-label="`Archive ${row.subject || '(no subject)'}`"
											:disabled="busy !== null"
											@click="archive(row)"
										>
											<Archive class="size-4" aria-hidden="true" />
										</Button>
										<Button
											v-else
											type="button"
											size="sm"
											variant="ghost"
											data-row-restore
											:aria-label="`Move ${row.subject || '(no subject)'} to the inbox`"
											:disabled="busy !== null"
											@click="restore(row)"
										>
											<ArchiveRestore class="size-4" aria-hidden="true" />
										</Button>
									</template>
									<Button
										type="button"
										size="sm"
										variant="ghost"
										data-row-delete
										:aria-label="`Delete ${row.subject || '(no subject)'}`"
										:disabled="busy !== null"
										@click="pendingDelete = row"
									>
										<Trash2 class="size-4" aria-hidden="true" />
									</Button>
								</div>
							</div>
						</li>
					</ul>
				</ZhPanel>
				<div
					v-if="!error && total > 0"
					data-testid="mail-pagination"
					class="flex flex-wrap items-center justify-between gap-3 text-sm"
				>
					<p class="text-muted-foreground">Showing {{ from }}–{{ to }} of {{ total }} · Page {{ page }} of {{ pages }}</p>
					<div class="flex gap-2">
						<Button
							type="button"
							size="sm"
							variant="outline"
							aria-label="Previous mail page"
							:disabled="page <= 1 || loading"
							@click="load(page - 1)"
						>
							Previous
						</Button>
						<Button
							type="button"
							size="sm"
							variant="outline"
							aria-label="Next mail page"
							:disabled="page >= pages || loading"
							@click="load(page + 1)"
						>
							Next
						</Button>
					</div>
				</div>
			</div>

			<Dialog :open="Boolean(openEmail)" @update:open="isOpen => { if (!isOpen) closeOpen() }">
				<!-- A fixed panel's percentage height is of the viewport: a long mail scrolls inside four fifths of it. -->
				<DialogContent v-if="openEmail" class="max-h-4/5 overflow-y-auto sm:max-w-2xl" data-testid="mail-open">
					<DialogHeader>
						<DialogTitle>{{ openEmail.subject || '(no subject)' }}</DialogTitle>
						<DialogDescription>
							From {{ openEmail.fromName || openEmail.fromAddress }} to {{ openEmail.envelopeTo }} · {{ sentAt(openEmail.receivedAt) }}
						</DialogDescription>
					</DialogHeader>
					<RouterLink
						v-if="source.messageUrl"
						:to="source.messageUrl(openEmail.id)"
						class="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
						data-testid="mail-full-screen"
					>
						<Maximize2 class="size-3.5" aria-hidden="true" />
						Open full screen
					</RouterLink>
					<ReuseMessage />
				</DialogContent>
			</Dialog>
		</template>

		<Dialog :open="composeOpen" @update:open="isOpen => { composeOpen = isOpen }">
			<DialogContent class="sm:max-w-lg" data-testid="mail-compose-dialog">
				<form class="space-y-3" data-testid="mail-compose-form" @submit.prevent="sendNew">
					<DialogHeader>
						<DialogTitle>New mail</DialogTitle>
						<DialogDescription>
								{{ own ? 'Sent from your own address, and filed in your mailbox.' : 'Sent from this mailbox’s address, and filed in it.' }}
							</DialogDescription>
					</DialogHeader>
					<div class="grid gap-1 text-sm">
						<div class="flex items-center justify-between">
							<span>To</span>
							<button v-if="!composeCopies" type="button" class="text-muted-foreground text-xs underline underline-offset-2" data-testid="mail-compose-copies" @click="composeCopies = true">Cc/Bcc</button>
						</div>
						<MailRecipientsField v-model="composeTo" :search="recipientSearch" :disabled="busy !== null" required label="To" testid="mail-compose-to" />
					</div>
					<template v-if="composeCopies">
						<div class="grid gap-1 text-sm">
							<span>Cc</span>
							<MailRecipientsField v-model="composeCc" :search="recipientSearch" :disabled="busy !== null" label="Cc" testid="mail-compose-cc" />
						</div>
						<div class="grid gap-1 text-sm">
							<span>Bcc</span>
							<MailRecipientsField v-model="composeBcc" :search="recipientSearch" :disabled="busy !== null" label="Bcc" testid="mail-compose-bcc" />
						</div>
					</template>
					<label class="grid gap-1 text-sm">
						From
						<Input v-model="composeFrom" :placeholder="own ? 'Your own address' : 'This mailbox’s address'" :disabled="busy !== null" data-testid="mail-compose-from" />
					</label>
					<label class="grid gap-1 text-sm">
						Subject
						<Input v-model="composeSubject" data-testid="mail-compose-subject" />
					</label>
					<div class="grid gap-1 text-sm">
						<span>Message</span>
						<MailEditor v-model="composeDraft" label="Message" :disabled="busy !== null" testid="mail-compose-text" />
					</div>
						<MailSignatureToggle v-model="addSignature" :signature="signature" :disabled="busy !== null" testid="mail-compose-signature" />
					<MailAttachmentPicker v-model="composeFiles" :disabled="busy !== null" />
					<p v-if="composeError" class="text-destructive text-sm" data-testid="mail-compose-error">{{ composeError }}</p>
					<DialogFooter>
						<Button type="button" size="sm" variant="outline" :disabled="busy !== null" @click="composeOpen = false">Cancel</Button>
						<Button type="submit" size="sm" :disabled="busy !== null || composeTo.length === 0 || draftIsBlank(composeDraft)">
							Send
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>

		<Dialog :open="Boolean(pendingDelete)" @update:open="isOpen => { if (!isOpen) pendingDelete = null }">
			<DialogContent v-if="pendingDelete" data-testid="mail-delete-dialog">
				<DialogHeader>
					<DialogTitle>Delete {{ pendingDelete.subject || 'this mail' }}?</DialogTitle>
					<DialogDescription>
						The mail, its files and its place in the conversation are removed for good. Archive it instead to keep it.
					</DialogDescription>
				</DialogHeader>
				<ZhSystemLabel tone="muted">This cannot be undone</ZhSystemLabel>
				<DialogFooter>
					<Button type="button" size="sm" variant="outline" :disabled="busy !== null" @click="pendingDelete = null">Cancel</Button>
					<Button type="button" size="sm" variant="destructive" data-testid="mail-delete-confirm" :disabled="busy !== null" @click="confirmDelete">
						<span aria-hidden="true">⚠</span>
						Delete
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>

		<Dialog :open="Boolean(clonePrompt)" @update:open="isOpen => { if (!isOpen) clonePrompt = null }">
			<DialogContent v-if="clonePrompt" data-testid="mailbox-action-clone">
				<DialogHeader>
					<DialogTitle>{{ clonePrompt.offer.label }} needs {{ clonePrompt.offer.name }}</DialogTitle>
					<DialogDescription v-if="cloneState === 'cloned'">
						{{ clonePrompt.offer.name }} is now one of your tasks. {{ clonePrompt.offer.label }} starts it from here.
					</DialogDescription>
					<DialogDescription v-else>
						{{ clonePrompt.offer.name }} is a task in the Catalog that no task of yours does yet. Clone it to use
						{{ clonePrompt.offer.label }}; it runs as the role it was published for.
					</DialogDescription>
				</DialogHeader>
				<p v-if="cloneError" class="text-destructive text-sm" data-testid="mailbox-action-clone-error">{{ cloneError }}</p>
				<DialogFooter>
					<RouterLink
						class="text-sm underline underline-offset-2 self-center"
						:to="{ path: '/catalog', query: { kind: 'task', search: clonePrompt.offer.slug } }"
						data-testid="mailbox-action-catalog"
					>
						Find it in the Catalog
					</RouterLink>
					<Button type="button" size="sm" variant="outline" :disabled="busy !== null" @click="clonePrompt = null">Close</Button>
					<Button
						v-if="cloneState === 'cloned' && clonedTaskId"
						type="button"
						size="sm"
						data-testid="mailbox-action-run"
						:disabled="busy !== null"
						@click="runCloned"
					>
						Run it now
					</Button>
					<Button
						v-else-if="source.cloneAction"
						type="button"
						size="sm"
						data-testid="mailbox-action-clone-confirm"
						:disabled="cloneState !== 'idle'"
						@click="cloneOffer"
					>
						{{ cloneState === 'cloning' ? 'Cloning…' : `Clone ${clonePrompt.offer.name}` }}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	</div>
</template>
